import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiLayers, FiInfo, FiBarChart2, FiArrowUp, FiArrowDown, 
  FiMinus, FiAlertCircle, FiAlertTriangle, FiCheckCircle
} = FiIcons;

const SyntaxComplexityAnalysis = ({ questions }) => {
  const [showHelp, setShowHelp] = useState(false);
  
  // Perform syntax complexity analysis
  const analyzedQuestions = questions.map(question => {
    const text = question.text || '';
    
    // Skip empty questions
    if (!text.trim()) {
      return {
        ...question,
        wordCount: 0,
        sentenceCount: 0,
        avgWordLength: 0,
        avgSentenceLength: 0,
        clauseDensity: 0,
        subordinationIndex: 0,
        complexityScore: 0,
        complexityLevel: 'unknown'
      };
    }
    
    // Basic metrics
    const words = text.trim().split(/\s+/);
    const wordCount = words.length;
    
    // Count characters excluding spaces
    const charCount = text.replace(/\s/g, '').length;
    
    // Average word length
    const avgWordLength = parseFloat((charCount / wordCount).toFixed(1));
    
    // Count sentences (periods, exclamation marks, question marks followed by space or end of text)
    const sentences = text.split(/[.!?](?:\s|$)/).filter(s => s.trim().length > 0);
    const sentenceCount = sentences.length || 1; // Prevent division by zero
    
    // Average words per sentence
    const avgSentenceLength = parseFloat((wordCount / sentenceCount).toFixed(1));
    
    // Count clauses (rough approximation - looking for verbs/verb phrases)
    // This is a simplified approach - a real NLP parser would do better
    const verbIndicators = [
      'is', 'are', 'was', 'were', 'be', 'been', 'being',
      'have', 'has', 'had', 'do', 'does', 'did',
      'can', 'could', 'will', 'would', 'shall', 'should',
      'may', 'might', 'must', 'ought'
    ];
    
    const lowerCaseText = text.toLowerCase();
    let clauseCount = 0;
    
    // Count verb indicators as potential clauses
    verbIndicators.forEach(verb => {
      const regex = new RegExp(`\\b${verb}\\b`, 'g');
      const matches = lowerCaseText.match(regex);
      if (matches) clauseCount += matches.length;
    });
    
    // Adjust for potential over-counting
    clauseCount = Math.min(clauseCount, wordCount / 3);
    clauseCount = Math.max(clauseCount, sentenceCount); // At least one clause per sentence
    
    // Clause density (clauses per sentence)
    const clauseDensity = parseFloat((clauseCount / sentenceCount).toFixed(2));
    
    // Subordination index (estimate based on certain connectors)
    const subordinators = [
      'although', 'though', 'because', 'since', 'when', 'while',
      'if', 'unless', 'until', 'where', 'whereas', 'that'
    ];
    
    let subordinateClauseCount = 0;
    subordinators.forEach(sub => {
      const regex = new RegExp(`\\b${sub}\\b`, 'gi');
      const matches = text.match(regex);
      if (matches) subordinateClauseCount += matches.length;
    });
    
    // Subordination index (subordinate clauses per clause)
    const subordinationIndex = parseFloat((subordinateClauseCount / clauseCount).toFixed(2));
    
    // Count complex words (words with 3+ syllables, approximated by length)
    const complexWords = words.filter(word => word.length > 8).length;
    const complexWordRatio = parseFloat((complexWords / wordCount).toFixed(2));
    
    // Compute overall complexity score (weighted sum of metrics)
    const complexityScore = parseFloat((
      (0.2 * avgWordLength) + 
      (0.3 * avgSentenceLength / 10) + 
      (0.3 * clauseDensity) + 
      (0.2 * subordinationIndex) +
      (0.2 * complexWordRatio)
    ).toFixed(2));
    
    // Determine complexity level
    let complexityLevel = 'medium';
    if (complexityScore < 0.5) complexityLevel = 'simple';
    else if (complexityScore < 0.8) complexityLevel = 'medium';
    else if (complexityScore < 1.2) complexityLevel = 'moderately complex';
    else complexityLevel = 'very complex';
    
    return {
      ...question,
      wordCount,
      sentenceCount,
      avgWordLength,
      avgSentenceLength,
      clauseDensity,
      subordinationIndex,
      complexityScore,
      complexityLevel,
      complexWordRatio
    };
  });
  
  // Calculate overall statistics
  const validQuestions = analyzedQuestions.filter(q => q.wordCount > 0);
  const avgComplexityScore = parseFloat(
    (validQuestions.reduce((sum, q) => sum + q.complexityScore, 0) / validQuestions.length).toFixed(2)
  );
  
  const avgClauseDensity = parseFloat(
    (validQuestions.reduce((sum, q) => sum + q.clauseDensity, 0) / validQuestions.length).toFixed(2)
  );
  
  const avgSubordinationIndex = parseFloat(
    (validQuestions.reduce((sum, q) => sum + q.subordinationIndex, 0) / validQuestions.length).toFixed(2)
  );
  
  const avgSentenceLength = parseFloat(
    (validQuestions.reduce((sum, q) => sum + q.avgSentenceLength, 0) / validQuestions.length).toFixed(1)
  );
  
  // Count questions by complexity level
  const complexityCounts = {
    simple: validQuestions.filter(q => q.complexityLevel === 'simple').length,
    medium: validQuestions.filter(q => q.complexityLevel === 'medium').length,
    moderatelyComplex: validQuestions.filter(q => q.complexityLevel === 'moderately complex').length,
    veryComplex: validQuestions.filter(q => q.complexityLevel === 'very complex').length
  };
  
  // Get color for complexity level
  const getComplexityColor = (level) => {
    switch (level) {
      case 'simple': return 'text-green-600';
      case 'medium': return 'text-blue-600';
      case 'moderately complex': return 'text-yellow-600';
      case 'very complex': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };
  
  // Get background color for complexity level
  const getComplexityBgColor = (level) => {
    switch (level) {
      case 'simple': return 'bg-green-50';
      case 'medium': return 'bg-blue-50';
      case 'moderately complex': return 'bg-yellow-50';
      case 'very complex': return 'bg-red-50';
      default: return 'bg-gray-50';
    }
  };
  
  // Get icon for complexity level
  const getComplexityIcon = (level) => {
    switch (level) {
      case 'simple': return FiCheckCircle;
      case 'medium': return FiCheckCircle;
      case 'moderately complex': return FiAlertCircle;
      case 'very complex': return FiAlertTriangle;
      default: return FiInfo;
    }
  };
  
  // Get recommendation based on complexity
  const getRecommendation = (question) => {
    const { complexityLevel, complexityScore, clauseDensity, subordinationIndex, avgSentenceLength } = question;
    
    if (complexityLevel === 'simple') {
      return "This item has simple syntax. Consider if it matches the intended difficulty level and cognitive demands.";
    } else if (complexityLevel === 'very complex') {
      if (clauseDensity > 2.5) {
        return "High clause density may make this item difficult to process. Consider simplifying some sentences.";
      } else if (subordinationIndex > 0.5) {
        return "High subordination makes this item structurally complex. Consider reducing nested clauses.";
      } else if (avgSentenceLength > 25) {
        return "Very long sentences may impede comprehension. Consider breaking down some sentences.";
      }
      return "This item has complex syntax that may challenge some students. Review if this matches your intent.";
    } else if (complexityLevel === 'moderately complex') {
      return "This item has appropriately challenging syntax for higher-level assessments.";
    } else {
      return "This item has balanced syntactic complexity suitable for most assessment contexts.";
    }
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <div className="p-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg text-white mr-3">
              <SafeIcon icon={FiLayers} />
            </div>
            Syntactic Complexity Analysis
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Evaluates the structural complexity of questions using linguistic metrics
          </p>
        </div>
        <div className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
          Linguistic Analysis
        </div>
      </div>

      {/* Help Section Toggle */}
      <div className="mb-6">
        <button 
          onClick={() => setShowHelp(!showHelp)}
          className="text-sm flex items-center text-purple-600 hover:text-purple-800"
        >
          <SafeIcon icon={FiInfo} className="mr-1" />
          <span>{showHelp ? 'Hide explanation' : 'What is syntactic complexity?'}</span>
        </button>
        
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 bg-purple-50 p-4 rounded-lg"
          >
            <h4 className="text-sm font-semibold text-purple-800 mb-2">Understanding Syntactic Complexity</h4>
            <p className="text-xs text-purple-700 mb-3">
              Syntactic complexity refers to the grammatical and structural sophistication of language. 
              It affects how difficult a question is to process and comprehend, regardless of the conceptual difficulty.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-3 rounded border border-purple-100">
                <h5 className="text-xs font-medium text-purple-800">Key Metrics</h5>
                <ul className="text-xs text-gray-600 list-disc pl-4 space-y-1 mt-1">
                  <li><strong>Clause Density:</strong> Average number of clauses per sentence</li>
                  <li><strong>Subordination Index:</strong> Proportion of subordinate clauses</li>
                  <li><strong>Sentence Length:</strong> Average words per sentence</li>
                  <li><strong>Word Length:</strong> Average characters per word</li>
                </ul>
              </div>
              
              <div className="bg-white p-3 rounded border border-purple-100">
                <h5 className="text-xs font-medium text-purple-800">Complexity Levels</h5>
                <ul className="text-xs space-y-1 mt-1">
                  <li className="text-green-700">Simple: Direct questions with simple structures</li>
                  <li className="text-blue-700">Medium: Balanced complexity suitable for most contexts</li>
                  <li className="text-yellow-700">Moderately Complex: Challenging but appropriate for higher levels</li>
                  <li className="text-red-700">Very Complex: May impede comprehension and add construct-irrelevant difficulty</li>
                </ul>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-100">
          <div className="text-sm text-purple-800 font-medium mb-1">Average Complexity</div>
          <div className="text-2xl font-bold text-indigo-900">{avgComplexityScore}</div>
          <div className="text-xs text-purple-700 mt-1">
            {avgComplexityScore < 0.5 ? 'Simple' : 
             avgComplexityScore < 0.8 ? 'Medium' : 
             avgComplexityScore < 1.2 ? 'Moderately complex' : 
             'Very complex'} question set
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-100">
          <div className="text-sm text-blue-800 font-medium mb-1">Clause Density</div>
          <div className="text-2xl font-bold text-blue-900">{avgClauseDensity}</div>
          <div className="text-xs text-blue-700 mt-1">
            Clauses per sentence
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 p-4 rounded-lg border border-indigo-100">
          <div className="text-sm text-indigo-800 font-medium mb-1">Subordination Index</div>
          <div className="text-2xl font-bold text-indigo-900">{avgSubordinationIndex}</div>
          <div className="text-xs text-indigo-700 mt-1">
            Subordinate clauses per clause
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-fuchsia-50 p-4 rounded-lg border border-purple-100">
          <div className="text-sm text-purple-800 font-medium mb-1">Complexity Distribution</div>
          <div className="flex items-center">
            <div className="h-4 bg-green-500 rounded-l" style={{width: `${(complexityCounts.simple/validQuestions.length)*100}%`}}></div>
            <div className="h-4 bg-blue-500" style={{width: `${(complexityCounts.medium/validQuestions.length)*100}%`}}></div>
            <div className="h-4 bg-yellow-500" style={{width: `${(complexityCounts.moderatelyComplex/validQuestions.length)*100}%`}}></div>
            <div className="h-4 bg-red-500 rounded-r" style={{width: `${(complexityCounts.veryComplex/validQuestions.length)*100}%`}}></div>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span className="text-green-700">{complexityCounts.simple} simple</span>
            <span className="text-blue-700">{complexityCounts.medium} medium</span>
            <span className="text-yellow-700">{complexityCounts.moderatelyComplex} mod.</span>
            <span className="text-red-700">{complexityCounts.veryComplex} complex</span>
          </div>
        </div>
      </div>

      {/* Complexity Distribution Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Complexity Score Distribution</h4>
        <div className="h-40 bg-gray-50 rounded-lg border border-gray-200 p-4 relative">
          {/* X-axis labels */}
          <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4">
            <span className="text-xs text-gray-500">0.0</span>
            <span className="text-xs text-gray-500">0.5</span>
            <span className="text-xs text-gray-500">1.0</span>
            <span className="text-xs text-gray-500">1.5</span>
            <span className="text-xs text-gray-500">2.0</span>
          </div>
          
          {/* X-axis line */}
          <div className="absolute bottom-4 left-4 right-4 h-px bg-gray-300"></div>
          
          {/* Complexity level regions */}
          <div className="absolute bottom-5 left-4 right-4 h-1 flex">
            <div className="bg-green-300 h-full" style={{width: '25%'}}></div>
            <div className="bg-blue-300 h-full" style={{width: '15%'}}></div>
            <div className="bg-yellow-300 h-full" style={{width: '20%'}}></div>
            <div className="bg-red-300 h-full flex-1"></div>
          </div>
          
          {/* Plot points */}
          {validQuestions.map((question, index) => {
            // Position based on complexity score (0-2 range mapped to 0-100% of width)
            const xPosition = (question.complexityScore / 2) * 100;
            // Random Y position for visual spread
            const yPosition = 20 + Math.random() * 50;
            
            return (
              <div 
                key={question.id}
                className={`absolute w-3 h-3 rounded-full border-2 border-white ${
                  question.complexityLevel === 'simple' ? 'bg-green-500' :
                  question.complexityLevel === 'medium' ? 'bg-blue-500' :
                  question.complexityLevel === 'moderately complex' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ 
                  left: `calc(${xPosition}% + 4px)`, 
                  bottom: `calc(${yPosition}% + 5px)`,
                  transform: 'translate(-50%, 50%)'
                }}
                title={`${question.text?.substring(0, 30)}... (Score: ${question.complexityScore})`}
              ></div>
            );
          })}
          
          {/* Level labels */}
          <div className="absolute top-2 left-4 right-4 flex text-xs text-gray-600">
            <div style={{width: '25%'}}>Simple</div>
            <div style={{width: '15%'}}>Medium</div>
            <div style={{width: '20%'}}>Moderate</div>
            <div className="flex-1">Very Complex</div>
          </div>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="overflow-x-auto mb-6">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 font-medium text-gray-700">Question</th>
              <th className="px-4 py-3 font-medium text-gray-700">Complexity</th>
              <th className="px-4 py-3 font-medium text-gray-700">Score</th>
              <th className="px-4 py-3 font-medium text-gray-700">Words</th>
              <th className="px-4 py-3 font-medium text-gray-700">Sentences</th>
              <th className="px-4 py-3 font-medium text-gray-700">Words/Sent</th>
              <th className="px-4 py-3 font-medium text-gray-700">Clause Density</th>
              <th className="px-4 py-3 font-medium text-gray-700">Subord. Index</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {analyzedQuestions.map((question) => (
              <tr key={question.id} className={`hover:bg-gray-50 transition-colors ${
                question.wordCount === 0 ? '' : getComplexityBgColor(question.complexityLevel) + '/10'
              }`}>
                <td className="px-4 py-3 text-gray-800">
                  {question.text?.length > 60 ? `${question.text.substring(0, 60)}...` : question.text || 'No text'}
                </td>
                <td className="px-4 py-3">
                  {question.wordCount > 0 ? (
                    <div className="flex items-center">
                      <SafeIcon 
                        icon={getComplexityIcon(question.complexityLevel)} 
                        className={`mr-1 ${getComplexityColor(question.complexityLevel)}`} 
                      />
                      <span className={`font-medium ${getComplexityColor(question.complexityLevel)}`}>
                        {question.complexityLevel.charAt(0).toUpperCase() + question.complexityLevel.slice(1)}
                      </span>
                    </div>
                  ) : (
                    <span className="text-gray-400">N/A</span>
                  )}
                </td>
                <td className="px-4 py-3 font-medium">
                  {question.wordCount > 0 ? question.complexityScore : 'N/A'}
                </td>
                <td className="px-4 py-3">
                  {question.wordCount > 0 ? question.wordCount : 'N/A'}
                </td>
                <td className="px-4 py-3">
                  {question.wordCount > 0 ? question.sentenceCount : 'N/A'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className="font-medium">{question.wordCount > 0 ? question.avgSentenceLength : 'N/A'}</span>
                    {question.wordCount > 0 && (
                      <>
                        {question.avgSentenceLength > 20 ? (
                          <SafeIcon icon={FiArrowUp} className="ml-1 text-red-500" />
                        ) : question.avgSentenceLength < 10 ? (
                          <SafeIcon icon={FiArrowDown} className="ml-1 text-green-500" />
                        ) : (
                          <SafeIcon icon={FiMinus} className="ml-1 text-gray-500" />
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className="font-medium">{question.wordCount > 0 ? question.clauseDensity : 'N/A'}</span>
                    {question.wordCount > 0 && (
                      <>
                        {question.clauseDensity > 2.0 ? (
                          <SafeIcon icon={FiArrowUp} className="ml-1 text-red-500" />
                        ) : question.clauseDensity < 1.2 ? (
                          <SafeIcon icon={FiArrowDown} className="ml-1 text-green-500" />
                        ) : (
                          <SafeIcon icon={FiMinus} className="ml-1 text-gray-500" />
                        )}
                      </>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <span className="font-medium">{question.wordCount > 0 ? question.subordinationIndex : 'N/A'}</span>
                    {question.wordCount > 0 && (
                      <>
                        {question.subordinationIndex > 0.5 ? (
                          <SafeIcon icon={FiArrowUp} className="ml-1 text-red-500" />
                        ) : question.subordinationIndex < 0.1 ? (
                          <SafeIcon icon={FiArrowDown} className="ml-1 text-green-500" />
                        ) : (
                          <SafeIcon icon={FiMinus} className="ml-1 text-gray-500" />
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recommendations */}
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Recommendations</h4>
        <div className="space-y-3">
          {validQuestions
            .filter(q => q.complexityLevel === 'very complex' || q.complexityLevel === 'simple')
            .slice(0, 5)
            .map(question => (
              <div key={question.id} className={`p-4 rounded-lg ${getComplexityBgColor(question.complexityLevel)}`}>
                <p className="text-sm text-gray-800 mb-2 font-medium">
                  {question.text?.substring(0, 100)}{question.text?.length > 100 ? '...' : ''}
                </p>
                <div className="flex items-start">
                  <SafeIcon 
                    icon={getComplexityIcon(question.complexityLevel)} 
                    className={`mt-0.5 mr-2 ${getComplexityColor(question.complexityLevel)}`} 
                  />
                  <p className="text-sm text-gray-700">
                    {getRecommendation(question)}
                  </p>
                </div>
                <div className="mt-2 text-xs text-gray-600 flex flex-wrap gap-2">
                  <span className="px-2 py-1 bg-white/50 rounded">
                    Complexity: {question.complexityScore}
                  </span>
                  <span className="px-2 py-1 bg-white/50 rounded">
                    Words/Sentence: {question.avgSentenceLength}
                  </span>
                  <span className="px-2 py-1 bg-white/50 rounded">
                    Clause Density: {question.clauseDensity}
                  </span>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Information Section */}
      <div className="mt-6 bg-purple-50 p-4 rounded-lg border border-purple-100">
        <h4 className="text-sm font-semibold text-purple-800 mb-2 flex items-center">
          <SafeIcon icon={FiInfo} className="mr-2" />
          About Syntactic Complexity Analysis
        </h4>
        <p className="text-xs text-purple-700">
          Syntactic complexity analysis evaluates the grammatical and structural sophistication of language used in assessment items. 
          Higher complexity can increase cognitive load independent of content difficulty, potentially affecting item validity.
          This analysis helps ensure that language complexity aligns with assessment goals and doesn't introduce construct-irrelevant variance.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="bg-white p-3 rounded border border-purple-100">
            <h5 className="text-xs font-medium text-purple-800">Clause Density</h5>
            <p className="text-xs text-gray-600">
              Measures clauses per sentence. Higher values indicate more propositional content packed into each sentence.
            </p>
          </div>
          <div className="bg-white p-3 rounded border border-purple-100">
            <h5 className="text-xs font-medium text-purple-800">Subordination Index</h5>
            <p className="text-xs text-gray-600">
              Ratio of dependent to independent clauses. Higher values indicate more hierarchical sentence structure.
            </p>
          </div>
          <div className="bg-white p-3 rounded border border-purple-100">
            <h5 className="text-xs font-medium text-purple-800">Sentence Length</h5>
            <p className="text-xs text-gray-600">
              Average words per sentence. Longer sentences generally require more working memory to process.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SyntaxComplexityAnalysis;